import React, { useState, useEffect, useRef } from 'react';
import {
    TextField as MuiTextField,
    Autocomplete,
    AutocompleteRenderInputParams,
    InputAdornment,
    Typography,
} from '@mui/material';
import { fieldToAutocomplete, AutocompleteProps } from 'formik-mui';

import useFocusOnError from '../../../components/formik/use-focus-on-error';
import { ListboxComponent, StyledPopper } from './bank-selector-virtualization';
import { BankOption } from './bank-selector-field.types';
import { getList } from './bank-selector-field.utils';
import { useGetBankListQuery } from 'features/profile/api/bank.api';
import { BankLogo } from '../../../components/formik/bank-selector/bank-logo/bank-logo';
import { getIn } from 'formik';
import FieldSkeletonLayout from '../../../components/layouts/field-skeleton-layout';

export const BankSelectorField: React.FC<any> = (
    props: AutocompleteProps<any, undefined, undefined, undefined> & {
        label: string;
        helpertext: string;
        issearch?: 'true' | 'false';
        isLoading?: 'true' | 'false';
    },
) => {
    const {
        form: { touched, errors, status, setFieldValue, setStatus },
        field: { name, value },
        label,
        helpertext,
        issearch = 'false',
        isLoading = 'false',
    } = props;
    const ref = useRef();

    const fieldTouched = getIn(touched, name);
    const fieldError = getIn(errors, name);
    const fieldStatus = getIn(status, name);

    useFocusOnError({
        fieldRef: ref,
        name,
    });

    const { data: bankList, isFetching } = useGetBankListQuery();

    const [options, updateOptions] = useState<any>([]);
    const [inputFocused, setInputFocused] = useState(false);
    const [selectedBank, setSelectedBank] = useState<BankOption | undefined>(
        undefined,
    );

    useEffect(() => {
        if (bankList && bankList.length > 0) {
            updateOptions(getList(bankList, issearch));
        }
    }, [bankList]);

    useEffect(() => {
        if (value) {
            const selectedBank = options.find(
                (bank: BankOption) => bank?.id === value,
            );

            setSelectedBank(selectedBank);
        } else {
            setSelectedBank(undefined);
        }
    }, [options, value]);

    useEffect(() => {
        if (fieldStatus) {
            const { [name]: _, ...newStatus } = status;
            setStatus(newStatus);
        }
    }, [selectedBank]);

    const onChange: any = React.useCallback(
        (event: any, option: BankOption) => {
            setFieldValue(name, option ? option.id : null);
        },
        [setFieldValue, name],
    );

    const autocompleteFieldProps = fieldToAutocomplete(props);

    const onFocus: any = React.useCallback((event: any) => {
        setInputFocused(true);
        if (event?.target?.autocomplete) {
            event.target.autocomplete = 'whatever';
        }
    }, []);

    const onBlur: any = React.useCallback(
        (event: any) => {
            setInputFocused(false);
            autocompleteFieldProps.onBlur?.(event);
        },
        [autocompleteFieldProps.onBlur],
    );

    const selectedOption =
        options.find((option: BankOption) => option.id === value) || null;

    return isLoading === 'true' ? (
        <FieldSkeletonLayout />
    ) : (
        <Autocomplete
            {...autocompleteFieldProps}
            options={options}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            value={selectedOption}
            disableListWrap
            PopperComponent={StyledPopper}
            ListboxComponent={ListboxComponent}
            isOptionEqualToValue={(option: BankOption, value: BankOption) =>
                option.id === value.id
            }
            autoComplete={false}
            getOptionLabel={(option: BankOption) => option.name}
            renderOption={(props, option, state) =>
                [props, option, state.index] as React.ReactNode
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
                <MuiTextField
                    {...params}
                    name={name}
                    error={
                        Boolean(fieldTouched) &&
                        (Boolean(fieldError) || Boolean(fieldStatus))
                    }
                    helperText={
                        helpertext ??
                        (fieldTouched && (fieldError || fieldStatus))
                    }
                    variant="outlined"
                    fullWidth
                    label={label}
                    inputRef={ref}
                    InputLabelProps={{
                        shrink: inputFocused || Boolean(selectedOption),
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                sx={{
                                    padding: '0px !important',
                                    transform: 'translateX(5px)',
                                }}>
                                <BankLogo bankName={selectedBank?.symbol} />
                            </InputAdornment>
                        ),
                        sx: {
                            '&.MuiOutlinedInput-root': {
                                padding: '9px 4px',
                            },
                        },
                    }}
                />
            )}
            noOptionsText={
                isFetching ? (
                    <Typography sx={{ color: 'text.primary' }}>
                        درحال دریافت اطلاعات...
                    </Typography>
                ) : (
                    <Typography sx={{ color: 'text.primary' }}>
                        هیچ بانکی یافت نشد
                    </Typography>
                )
            }
        />
    );
};
